import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { useEmpresasContext } from './EmpresasContext';
import { useAuth } from '../hooks/useAuth';
import { type UploadedKml } from '../services/kml/kmlService';
import { getCurrentDateISO } from '../utils/dateUtils';

// ===============================
// TIPOS PARA EL CONTEXTO
// ===============================

export interface CalificadorFaena {
  calificador_id: number;
  valor: string;
}

interface Faena {
  id_temporal: string;

  faena_id: number | null;
  predio_id: number | null;
  rodal_id: number | null;
  horas: number;

  // Información descriptiva
  descripcion: string;
  faena_codigo?: string;
  predio_codigo?: string;
  rodal_codigo?: string;

  // Área
  area_trabajada: number;
  unidad_medida: string;

  // 🗺️ KML MÚLTIPLE
  archivos_kml: UploadedKml[];
  area_kml_total: number;
  longitud_kml_total: number;

  // Calificadores opcionales (humedad, tipo terreno, tocones, actividad, etc.)
  calificadores: CalificadorFaena[];

  // Metadatos
  observaciones?: string;
}

interface Insumo {
  id_temporal: string;
  insumo_id: number;
  nombre_insumo: string;
  cantidad: number;
  unidad: string;
  costo_unitario?: number;
}

interface ItemChequeo {
  id_temporal: string;
  item_chequeo_id: number;
  nombre_item: string; // Solo para mostrar en frontend, no se envía al backend
  verificado: boolean; // Solo para mostrar en frontend, no se envía al backend
  horometro: number;   // Horómetro cuando se hizo el chequeo
  tiempo: number;      // Minutos que tomó el chequeo (1-480)
  observaciones?: string;
  requiere_atencion: boolean; // Solo para mostrar en frontend, no se envía al backend
}

interface ReporteEnMemoria {
  // 🏢 MULTITENANT - empresa_id se obtiene dinámicamente del contexto
  // Ya no se almacena en el estado del reporte, se obtiene de EmpresasContext
  
  // 📝 Información básica
  numero_reporte: string; // "123" (papel) o "e123" (electrónico)
  maquina_id: number | null;
  maquina_info?: {
    codigo: string;
    tipo: string;
    descripcion: string;
  };
  fecha_reporte: string;
  operador_id: number | null;
  operador_info?: {
    codigo: string;
    nombre: string;
  };
  
  // ⏱️ Horómetros
  horometro_inicial: number;
  horometro_final: number;
  horas_trabajadas: number; // Calculado automáticamente
  
  // 📋 Faenas (arrays dinámicos)
  faenas: Faena[];
  
  // 📦 Insumos (arrays dinámicos)
  insumos: Insumo[];
  
  // ✅ Items de chequeo (arrays dinámicos)
  items_chequeo: ItemChequeo[];
  
  // 🔧 Estado y observaciones
  estado_maquina: 'OPERATIVA' | 'FALLA_MENOR' | 'FALLA_MAYOR' | 'MANTENIMIENTO';
  observaciones: string;
  novedades: string;
  
  // 🏷️ Metadatos
  estado_reporte: 'borrador' | 'completo' | 'enviado';
  id_temporal: string;
  created_at: string;
  updated_at: string;
}

// ===============================
// ACCIONES DEL REDUCER
// ===============================

type ReporteAction =
  | { type: 'INICIALIZAR_REPORTE'; payload: Partial<ReporteEnMemoria> }
  | { type: 'CARGAR_REPORTE_EXISTENTE'; payload: ReporteEnMemoria } // 🆕 NUEVA ACCIÓN
  | { type: 'ACTUALIZAR_BASICO'; payload: Partial<ReporteEnMemoria> }
  | { type: 'ACTUALIZAR_HOROMETROS'; payload: { inicial: number; final: number } }
  | { type: 'AGREGAR_FAENA'; payload: Faena }
  | { type: 'ACTUALIZAR_FAENA'; payload: { id_temporal: string; faena: Partial<Faena> } }
  | { type: 'ELIMINAR_FAENA'; payload: { id_temporal: string } }
  | { type: 'AGREGAR_INSUMO'; payload: Insumo }
  | { type: 'ACTUALIZAR_INSUMO'; payload: { id_temporal: string; insumo: Partial<Insumo> } }
  | { type: 'ELIMINAR_INSUMO'; payload: { id_temporal: string } }
  | { type: 'AGREGAR_ITEM_CHEQUEO'; payload: ItemChequeo }
  | { type: 'ACTUALIZAR_ITEM_CHEQUEO'; payload: { id_temporal: string; item: Partial<ItemChequeo> } }
  | { type: 'ELIMINAR_ITEM_CHEQUEO'; payload: { id_temporal: string } }
  | { type: 'CAMBIAR_ESTADO'; payload: { estado: ReporteEnMemoria['estado_reporte'] } }
  | { type: 'LIMPIAR_REPORTE' }
  | { type: 'MARCAR_ENVIADO'; payload: { reporteId: number; numeroReporte: string } };

// ===============================
// ESTADO INICIAL (SIN EMPRESA_ID)
// ===============================

const estadoInicial: ReporteEnMemoria = {
  // Básico
  numero_reporte: '', // 🆕 Inicializar vacío
  maquina_id: null,
  fecha_reporte: getCurrentDateISO(),
  operador_id: null,
  
  // Horómetros
  horometro_inicial: 0,
  horometro_final: 0,
  horas_trabajadas: 0,
  
  // Arrays
  faenas: [],
  insumos: [],
  items_chequeo: [],
  
  // Estado
  estado_maquina: 'OPERATIVA',
  observaciones: '',
  novedades: '',
  
  // Metadatos
  estado_reporte: 'borrador',
  id_temporal: '',
  created_at: '',
  updated_at: '',
};

// ===============================
// REDUCER
// ===============================

function reporteReducer(state: ReporteEnMemoria, action: ReporteAction): ReporteEnMemoria {
  const now = new Date().toISOString();
  
  switch (action.type) {
    case 'INICIALIZAR_REPORTE':
      return {
        ...estadoInicial,
        ...action.payload,
        id_temporal: `temp_${Date.now()}`,
        numero_reporte: action.payload.numero_reporte || '', // 🆕 Default vacío
        created_at: now,
        updated_at: now,
      };
      
    case 'CARGAR_REPORTE_EXISTENTE': // 🆕 NUEVO CASO
      return {
        ...action.payload,
        estado_reporte: 'completo', // Marcar como completo ya que viene de BD
        updated_at: now,
      };
      
    case 'ACTUALIZAR_BASICO':
      return {
        ...state,
        ...action.payload,
        updated_at: now,
      };
      
    case 'ACTUALIZAR_HOROMETROS':
      const { inicial, final } = action.payload;
      return {
        ...state,
        horometro_inicial: inicial,
        horometro_final: final,
        horas_trabajadas: Math.round(Math.max(0, final - inicial) * 10) / 10, // Redondear a 1 decimal
        updated_at: now,
      };
      
    case 'AGREGAR_FAENA':
      return {
        ...state,
        faenas: [...state.faenas, action.payload],
        updated_at: now,
      };
      
    case 'ACTUALIZAR_FAENA':
      return {
        ...state,
        faenas: state.faenas.map(faena =>
          faena.id_temporal === action.payload.id_temporal
            ? action.payload.faena as Faena // ✅ CORREGIDO: Reemplazar completamente
            : faena
        ),
        updated_at: now,
      };
      
    case 'ELIMINAR_FAENA':
      return {
        ...state,
        faenas: state.faenas.filter(faena => faena.id_temporal !== action.payload.id_temporal),
        updated_at: now,
      };
      
    case 'AGREGAR_INSUMO':
      return {
        ...state,
        insumos: [...state.insumos, action.payload],
        updated_at: now,
      };
      
    case 'ACTUALIZAR_INSUMO':
      return {
        ...state,
        insumos: state.insumos.map(insumo =>
          insumo.id_temporal === action.payload.id_temporal
            ? { ...insumo, ...action.payload.insumo }
            : insumo
        ),
        updated_at: now,
      };
      
    case 'ELIMINAR_INSUMO':
      return {
        ...state,
        insumos: state.insumos.filter(insumo => insumo.id_temporal !== action.payload.id_temporal),
        updated_at: now,
      };
      
    case 'AGREGAR_ITEM_CHEQUEO':
      return {
        ...state,
        items_chequeo: [...state.items_chequeo, {
          ...action.payload,
          id_temporal: action.payload.id_temporal || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` // Generar si no existe
        }],
        updated_at: now,
      };
      
    case 'ACTUALIZAR_ITEM_CHEQUEO':
      return {
        ...state,
        items_chequeo: state.items_chequeo.map(item =>
          item.id_temporal === action.payload.id_temporal
            ? { ...item, ...action.payload.item }
            : item
        ),
        updated_at: now,
      };
      
    case 'ELIMINAR_ITEM_CHEQUEO':
      return {
        ...state,
        items_chequeo: state.items_chequeo.filter(item => item.id_temporal !== action.payload.id_temporal),
        updated_at: now,
      };
      
    case 'CAMBIAR_ESTADO':
      return {
        ...state,
        estado_reporte: action.payload.estado,
        updated_at: now,
      };
      
    case 'LIMPIAR_REPORTE':
      return estadoInicial;
      
    case 'MARCAR_ENVIADO':
      return {
        ...state,
        estado_reporte: 'enviado',
        updated_at: now,
      };
      
    default:
      return state;
  }
}

// ===============================
// CONTEXTO MULTITENANT
// ===============================

interface ReporteContextType {
  reporte: ReporteEnMemoria;
  dispatch: React.Dispatch<ReporteAction>;
  
  // 🏢 MULTITENANT: Información de empresa actual
  empresaId: number | null;
  nombreEmpresa: string;
  
  // Helpers calculados
  estaCompleto: boolean;
  totalAreaTrabajada: number;
  totalCostoInsumos: number;
  itemsChequeoCompletos: number;
  itemsChequeoTotal: number;
  
  // Validación de consistencia de horómetros
  validacionHorometros: {
    diferenciaHorometros: number;
    sumaHorasFaenas: number;
    diferencia: number;
    esValido: boolean;
    mensaje: string | null;
  };

  // 🎯 Validaciones multitenant
  puedeCrearReporte: boolean;
  puedeEditarReporte: boolean;
  tieneEmpresaActiva: boolean;
}

const ReporteContext = createContext<ReporteContextType | undefined>(undefined);

// ===============================
// PROVIDER MULTITENANT
// ===============================

interface ReporteProviderProps {
  children: ReactNode;
}

export const ReporteProvider: React.FC<ReporteProviderProps> = ({ children }) => {
  const [reporte, dispatch] = useReducer(reporteReducer, estadoInicial);
  
  // 🏢 MULTITENANT: Obtener información de empresa del contexto
  const { 
    empresaActiva, 
    nombreEmpresaActiva,
    error: empresaError 
  } = useEmpresasContext();
  
  // 🆕 NUEVO: Obtener permisos UAU directamente del auth
  const { usuario } = useAuth();
  
  // Información de empresa con validación más robusta
  const empresaId = empresaActiva?.empresa?.id || null;
  const nombreEmpresa = nombreEmpresaActiva || '';
  // Ser más permisivo: solo requerir que haya empresa, no necesariamente permisos perfectos
  const tieneEmpresaActiva = Boolean(empresaActiva && !empresaError);
  
  // 🔧 CORREGIDO: Obtener permisos UAU del usuario, no del empresaActiva
  const puedeCrearReporte = Boolean(
    usuario?.tipo_usuario === 'administrador' || 
    usuario?.permisos_menu?.includes('reportes.crear') ||  // 🔧 CORREGIDO: usar permisos_menu
    false
  );
  
  // 🆕 NUEVO: Verificar permisos de edición
  const puedeEditarReporte = Boolean(
    usuario?.tipo_usuario === 'administrador' || 
    usuario?.permisos_menu?.includes('reportes.editar') ||  // 🔧 CORREGIDO: usar permisos_menu
    usuario?.tipo_usuario === 'supervisor' || // Supervisores pueden editar
    false
  );
  
  // 🆕 NUEVA FUNCIÓN: Validar que horas de faenas coincidan con diferencia de horómetros
  const validarConsistenciaHorometros = (): boolean => {
    if (reporte.faenas.length === 0) return false;

    // Redondear a 1 decimal para evitar problemas de precisión flotante
    const diferenciaHorometros = Math.round((reporte.horometro_final - reporte.horometro_inicial) * 10) / 10;
    const sumaHorasFaenas = Math.round(reporte.faenas.reduce((total, faena) => total + faena.horas, 0) * 10) / 10;

    // ✅ SIN TOLERANCIA: Debe ser exactamente igual (después de redondear)
    return diferenciaHorometros === sumaHorasFaenas;
  };

  // 🆕 NUEVA FUNCIÓN: Obtener información detallada de validación
  const validacionHorometros = {
    diferenciaHorometros: Math.round((reporte.horometro_final - reporte.horometro_inicial) * 10) / 10,
    sumaHorasFaenas: Math.round(reporte.faenas.reduce((total, faena) => total + faena.horas, 0) * 10) / 10,
    diferencia: Math.round(Math.abs((reporte.horometro_final - reporte.horometro_inicial) - reporte.faenas.reduce((total, faena) => total + faena.horas, 0)) * 10) / 10,
    esValido: validarConsistenciaHorometros(),
    mensaje: !validarConsistenciaHorometros() && reporte.faenas.length > 0
      ? `Las horas de faenas (${Math.round(reporte.faenas.reduce((total, faena) => total + faena.horas, 0) * 10) / 10}h) deben coincidir EXACTAMENTE con las horas trabajadas (${Math.round((reporte.horometro_final - reporte.horometro_inicial) * 10) / 10}h)`
      : null
  };
  
  // Helpers calculados
  const estaCompleto = Boolean(
    tieneEmpresaActiva &&
    reporte.maquina_id &&
    reporte.fecha_reporte &&
    reporte.horometro_final > reporte.horometro_inicial &&
    reporte.faenas.length > 0 &&
    validarConsistenciaHorometros() // 🆕 NUEVA VALIDACIÓN
  );
  
  const totalAreaTrabajada = reporte.faenas.reduce(
    (total, faena) => total + faena.area_trabajada, 
    0
  );
  
  const totalCostoInsumos = reporte.insumos.reduce(
    (total, insumo) => total + (insumo.cantidad * (insumo.costo_unitario || 0)), 
    0
  );
  
  const itemsChequeoCompletos = reporte.items_chequeo.filter(item => item.verificado).length;
  const itemsChequeoTotal = reporte.items_chequeo.length;
  
  const contextValue: ReporteContextType = {
    reporte,
    dispatch,
    
    // 🏢 MULTITENANT
    empresaId,
    nombreEmpresa,
    
    // Helpers
    estaCompleto,
    totalAreaTrabajada,
    totalCostoInsumos,
    itemsChequeoCompletos,
    itemsChequeoTotal,
    
    // 🆕 NUEVA: Validación de horómetros
    validacionHorometros,
    
    // Validaciones multitenant
    puedeCrearReporte,
    puedeEditarReporte,  // 🆕 NUEVO
    tieneEmpresaActiva,
  };
  
  return (
    <ReporteContext.Provider value={contextValue}>
      {children}
    </ReporteContext.Provider>
  );
};

// ===============================
// HOOK PERSONALIZADO MULTITENANT
// ===============================

export const useReporte = (): ReporteContextType & { validacionHorometros: any } => {
  const context = useContext(ReporteContext);
  if (!context) {
    throw new Error('useReporte debe ser usado dentro de un ReporteProvider');
  }
  
  // 🏢 MULTITENANT: Validar empresa activa
  if (!context.tieneEmpresaActiva) {
    // Log removido para evitar warnings excesivos durante desarrollo
    // console.warn('⚠️ useReporte: No hay empresa activa');
  }
  
  return context as ReporteContextType & { validacionHorometros: any };
};

// ===============================
// ACTIONS HELPERS MULTITENANT
// ===============================

export const reporteActions = {
  inicializar: (data: Partial<ReporteEnMemoria> = {}) => ({
    type: 'INICIALIZAR_REPORTE' as const,
    payload: {
      ...data,
      // 🏢 NOTA: empresa_id se obtiene automáticamente del contexto al enviar
    },
  }),
  
  // 🆕 NUEVA ACCIÓN: Cargar reporte existente para edición
  cargarExistente: (reporteCompleto: ReporteEnMemoria) => ({
    type: 'CARGAR_REPORTE_EXISTENTE' as const,
    payload: reporteCompleto,
  }),
  
  actualizarBasico: (data: Partial<ReporteEnMemoria>) => ({
    type: 'ACTUALIZAR_BASICO' as const,
    payload: data,
  }),
  
  actualizarHorometros: (inicial: number, final: number) => ({
    type: 'ACTUALIZAR_HOROMETROS' as const,
    payload: { inicial, final },
  }),
  
  agregarFaena: (faena: Faena) => ({
    type: 'AGREGAR_FAENA' as const,
    payload: faena,
  }),
  
  actualizarFaena: (faenaActualizada: Faena) => ({
    type: 'ACTUALIZAR_FAENA' as const,
    payload: { id_temporal: faenaActualizada.id_temporal, faena: faenaActualizada }, // ✅ Enviar faena completa
  }),
  
  eliminarFaena: (payload: { id_temporal: string }) => ({
    type: 'ELIMINAR_FAENA' as const,
    payload: payload,
  }),
  
  agregarInsumo: (insumo: Insumo) => ({
    type: 'AGREGAR_INSUMO' as const,
    payload: insumo,
  }),

  actualizarInsumo: (insumoActualizado: { id_temporal: string; insumo: Partial<Insumo> }) => ({
    type: 'ACTUALIZAR_INSUMO' as const,
    payload: insumoActualizado,
  }),

  eliminarInsumo: (payload: { id_temporal: string }) => ({
    type: 'ELIMINAR_INSUMO' as const,
    payload: payload,
  }),
  
  agregarItemChequeo: (item: ItemChequeo) => ({
    type: 'AGREGAR_ITEM_CHEQUEO' as const,
    payload: item,
  }),

  actualizarItemChequeo: (id_temporal: string, item: Partial<ItemChequeo>) => ({
    type: 'ACTUALIZAR_ITEM_CHEQUEO' as const,
    payload: { id_temporal, item },
  }),

  eliminarItemChequeo: (id_temporal: string) => ({
    type: 'ELIMINAR_ITEM_CHEQUEO' as const,
    payload: { id_temporal },
  }),

  limpiar: () => ({
    type: 'LIMPIAR_REPORTE' as const,
  }),
  
  marcarEnviado: (reporteId: number, numeroReporte: string) => ({
    type: 'MARCAR_ENVIADO' as const,
    payload: { reporteId, numeroReporte },
  }),
};

export default ReporteContext;
